/**
 * App shell component
 *
 * Main layout wrapper that provides:
 * - Skip-to-content link (visible on focus) for keyboard users (WCAG 2.4.1)
 * - Header with branding, navigation (desktop), and user menu
 * - Bottom navigation (mobile only) with accessible landmark label
 * - Main content area with focus management on route changes (WCAG 2.4.3)
 * - Dynamic document title per route (WCAG 2.4.2)
 * - PWA install prompt and update notifications
 * - Offline status indicator
 *
 * Desktop (lg+): Top navigation in header, full-width content
 * Mobile/Tablet: Bottom navigation, header without nav links
 */
import type { ReactNode } from "react";
import { useLayoutEffect, useRef } from "react";
import { useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Header } from "./header";
import { BottomNav } from "./bottom-nav";
import { InstallPrompt, UpdatePrompt, OfflineIndicator } from "@/components/pwa";
import { useOnlineStatus, useDocumentTitle } from "@/hooks";
import { useAuth } from "@/providers/auth-provider";
import { useLanguageSync } from "@/hooks/use-language-sync";

interface AppShellProps {
  children: ReactNode;
}

/**
 * Main application shell layout
 *
 * Provides responsive layout with:
 * - Skip-to-content link for keyboard navigation (WCAG 2.4.1)
 * - Fixed glass-morphism header at top with navigation (desktop)
 * - Bottom nav on mobile with frosted glass treatment
 * - Scrollable main content area (full width on desktop)
 * - Dynamic document titles per route (WCAG 2.4.2)
 * - Focus moved to main on route change (WCAG 2.4.3)
 * - PWA install and update prompts
 * - Offline indicator banner
 *
 * The header and bottom nav are hidden on the unauthenticated landing page (`/`)
 * so that the LandingPage component provides its own full-bleed entrypoint UI.
 */
export function AppShell({ children }: AppShellProps) {
  const isOnline = useOnlineStatus();
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const { t } = useTranslation("common");
  const mainRef = useRef<HTMLElement | null>(null);
  const prevPathnameRef = useRef<string | null>(null);

  useLanguageSync({ enabled: isAuthenticated });

  // Set document.title per route and manage focus on navigation (WCAG 2.4.2 / 2.4.3)
  useDocumentTitle();

  /** Hide chrome (header + bottom nav) on the unauthenticated landing page */
  const isLandingPage = !isAuthenticated && location.pathname === "/";

  useLayoutEffect(() => {
    const mainEl = mainRef.current;
    if (!mainEl) return;

    const prevPathname = prevPathnameRef.current;
    prevPathnameRef.current = location.pathname;

    const isNewPage = prevPathname === null || prevPathname !== location.pathname;

    // Default: each page starts scrolled to the top.
    // Note: we keep scroll state on filters/search changes by only keying on pathname/hash.
    if (location.hash) {
      const id = location.hash.replace(/^#/, "");
      const target = id ? document.getElementById(id) : null;
      if (target) {
        target.scrollIntoView();
        return;
      }
    }

    if (!isNewPage) return;

    mainEl.scrollTo({ top: 0, left: 0 });
    window.scrollTo({ top: 0, left: 0 });
  }, [location.pathname, location.hash]);

  return (
    <div className="flex h-screen h-dvh flex-col overflow-hidden bg-background text-foreground">
      {/* Skip link — visible only on keyboard focus (WCAG 2.4.1) */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        {t("nav.skipToContent")}
      </a>

      {/* Offline indicator at the very top */}
      <OfflineIndicator isOnline={isOnline} />

      {/* Fixed header — hidden on the unauthenticated landing page */}
      {!isLandingPage && <Header />}

      {/* Main content - full width, no sidebar */}
      <main
        id="main-content"
        ref={mainRef}
        tabIndex={-1}
        className={
          isLandingPage
            ? "flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain outline-none"
            : "flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain pb-20 lg:pb-8 outline-none"
        }
      >
        {isLandingPage ? (
          children
        ) : (
          <div className="mx-auto max-w-screen-xl px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
            {children}

            {/* Legal footer — visible on desktop (hidden on mobile where bottom nav is used) */}
            <footer className="mt-12 hidden border-t pt-4 pb-2 text-center text-xs text-muted-foreground lg:block">
              <div className="flex items-center justify-center gap-3">
                <a href="/privacy" className="hover:text-foreground hover:underline underline-offset-4 transition-colors">
                  {t("legal.privacyPolicy")}
                </a>
                <span>·</span>
                <a href="/terms" className="hover:text-foreground hover:underline underline-offset-4 transition-colors">
                  {t("legal.termsOfService")}
                </a>
              </div>
            </footer>
          </div>
        )}
      </main>

      {/* Mobile bottom navigation — hidden on the unauthenticated landing page */}
      {!isLandingPage && <BottomNav />}

      {/* PWA prompts */}
      <InstallPrompt />
      <UpdatePrompt />
    </div>
  );
}
